import { NgIf } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';
import { SITE_SETTINGS } from '@app/constants';
import { ComponentCanDeactivate } from '@app/core/guards';
import { ReadOnly } from '@app/utils/claims';
import { ButtonBarComponent } from '../button-bar/button-bar.component';

@Component({
    selector: 'sl-rich-text-editor',
    templateUrl: './rich-text-editor.component.html',
    standalone: true,
    imports: [
        NgIf,
        FroalaEditorModule,
        ButtonBarComponent,
        FroalaViewModule,
    ],
})
export class RichTextEditorComponent implements OnInit, ComponentCanDeactivate {
	@ReadOnly()
	isReadOnly = true;

	@Input()
	get model(): string {
		return this.html;
	}
	set model(value: string) {
		this.originalHtml = value;
		this.html = value;
	}

	@Input() replacements: any;

	@Output() htmlUpdated: EventEmitter<string> = new EventEmitter<string>();
	@Output() contentChanged: EventEmitter<string> = new EventEmitter<string>();

	html: string;
	originalHtml: string;

	froalaOptions = {
		key: SITE_SETTINGS.froalaKey,
		attribution: false,
		iframe: true,
		heightMin: 300,
		placeholderText: '',
		//If the list of plugins is updated, be sure to change the imports in RichTextEditorModule
		pluginsEnabled: [
			'align',
			'codeBeautifier',
			'colors',
			'draggable',
			'fontFamily',
			'fontSize',
			'fullscreen',
			'help',
			'image',
			'lineBreaker',
			'lineHeight',
			'link',
			'lists',
			'paragraphFormat',
			'quickInsert',
			'quote',
			'table',
			'url',
			'wordPaste',
		],
		quickInsertButtons: ['image', 'table', 'ul', 'ol', 'hr'],
		toolbarButtons: [
			'fullscreen',
			'bold',
			'italic',
			'underline',
			'strikeThrough',
			'subscript',
			'superscript',
			'|',
			'fontFamily',
			'fontSize',
			'color',
			'lineHeight',
			'|',
			'paragraphFormat',
			'align',
			'formatOL',
			'formatUL',
			'outdent',
			'indent',
			'quote',
			'-',
			'insertLink',
			'insertImage',
			'insertTable',
			'|',
			'insertHR',
			'selectAll',
			'clearFormatting',
			'|',
			'undo',
			'redo',
			'replacements',
			'help',
		],
		events: {
			//To save the image as a base64 encoded data URI instead of uploaded blob
			'image.beforeUpload'(files: FileList): boolean {
				// eslint-disable-next-line @typescript-eslint/no-this-alias
				const editor = this;
				if (files.length) {
					const reader = new FileReader();
					reader.onload = (e: any) => {
						const result = e.target.result;
						editor.image.insert(result, null, null, editor.image.get());
					};
					reader.readAsDataURL(files[0]);
				}
				return false;
			},
		},
	};

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => this.originalHtml === this.html;

	ngOnInit(): void {
		if (this.replacements) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			FroalaEditor.DefineIcon('replacements', { NAME: 'cog', SVG_KEY: 'cogs' });

			FroalaEditor.RegisterCommand('replacements', {
				title: 'Insert Dynamic Properties',
				type: 'dropdown',
				focus: false,
				undo: false,
				refreshAfterCallback: true,
				options: this.replacements,
				callback(cmd, val) {
					const content = `{{${val}}}`;
					this.html.insert(content);
					this.undo.saveStep();
				},
			});
		}
	}

	onCancel(): void {
		this.html = this.originalHtml;
		this.contentChanged.emit(this.html);
	}

	onSubmit(): void {
		this.htmlUpdated.emit(this.html);
	}

	onContentChange(): void {
		this.contentChanged.emit(this.html);
	}
}
